import type { ReactNode } from "react";

export interface ArticleContentData {
  id: number;
  hero: {
    breadcrumb: string[];
    h1Lines: string[];
    date: string;
    readTime: string;
    category: string;
    imageUrl: string;
    imageAlt: string;
  };
  body: ReactNode;
}

export function getArticleContent(id: number): ArticleContentData | null {
  const map: Record<number, ArticleContentData> = {
    // Article 1: Buying Guide (already has its own page)
    // Article 2-5: Also go to existing buying guide page

    // Article 6: Shoes Guide
    6: {
      id: 6,
      hero: {
        breadcrumb: ["Home", "Blog", "Sneakers"],
        h1Lines: ["PANDABUY SHEET", "SNEAKER DATABASE", "COMPLETE GUIDE"],
        date: "June 15, 2026",
        readTime: "11 min read",
        category: "Sneakers",
        imageUrl: "https://readdy.ai/api/search-image?query=Artistic%20overhead%20flat%20lay%20of%20six%20different%20stylish%20sneakers%20arranged%20in%20a%20circular%20composition%20on%20warm%20concrete%20floor%20surface%20with%20soft%20morning%20light%20casting%20gentle%20directional%20shadows%20creative%20sneakerhead%20editorial%20photography%20with%20urban%20loft%20aesthetic%20and%20warm%20neutral%20earth%20tones&width=1920&height=1080&seq=pandabuy-article-hero-shoes-v2&orientation=landscape",
        imageAlt: "Pandabuy Spreadsheet Shoes Guide"
      },
      body: null
    },

    // Article 7: Hoodies Guide
    7: {
      id: 7,
      hero: {
        breadcrumb: ["Home", "Blog", "Hoodies & Streetwear"],
        h1Lines: ["PANDABUY SHEET", "HOODIES & STREETWEAR", "STYLE GUIDE"],
        date: "June 12, 2026",
        readTime: "9 min read",
        category: "Hoodies & Streetwear",
        imageUrl: "https://readdy.ai/api/search-image?query=Relaxed%20street%20style%20scene%20with%20three%20different%20colored%20oversized%20hoodies%20hanging%20on%20a%20minimal%20clothing%20rack%20in%20a%20sunlit%20industrial%20loft%20space%20with%20exposed%20brick%20and%20large%20windows%20warm%20golden%20afternoon%20light%20creating%20soft%20atmospheric%20glow%20casual%20fashion%20editorial%20with%20authentic%20urban%20character&width=1920&height=1080&seq=pandabuy-article-hero-hoodies-v2&orientation=landscape",
        imageAlt: "Pandabuy Spreadsheet Hoodies Guide"
      },
      body: null
    },

    // Article 8: T-Shirts Guide
    8: {
      id: 8,
      hero: {
        breadcrumb: ["Home", "Blog", "T-Shirts"],
        h1Lines: ["PANDABUY SHEET", "T-SHIRT COLLECTION", "BUYER'S GUIDE"],
        date: "June 10, 2026",
        readTime: "8 min read",
        category: "T-Shirts",
        imageUrl: "https://readdy.ai/api/search-image?query=Minimalist%20flat%20lay%20of%20six%20folded%20cotton%20t-shirts%20in%20various%20neutral%20colors%20stacked%20neatly%20on%20a%20light%20oak%20wooden%20surface%20with%20a%20sprig%20of%20dried%20lavender%20beside%20them%20soft%20diffused%20natural%20daylight%20creating%20clean%20shadow%20lines%20modern%20capsule%20wardrobe%20editorial%20photography%20with%20calm%20serene%20aesthetic&width=1920&height=1080&seq=pandabuy-article-hero-tshirts-v2&orientation=landscape",
        imageAlt: "Pandabuy Spreadsheet T-Shirts Guide"
      },
      body: null
    },

    // Article 9: Jackets Guide
    9: {
      id: 9,
      hero: {
        breadcrumb: ["Home", "Blog", "Jackets & Outerwear"],
        h1Lines: ["PANDABUY SHEET", "JACKETS & OUTERWEAR", "SEASONAL PICKS"],
        date: "June 8, 2026",
        readTime: "10 min read",
        category: "Jackets & Outerwear",
        imageUrl: "https://readdy.ai/api/search-image?query=Four%20distinctive%20outerwear%20pieces%20displayed%20on%20a%20sleek%20black%20garment%20rack%20in%20a%20bright%20white%20minimalist%20studio%20space%20with%20polished%20concrete%20floor%20large%20windows%20flooding%20the%20room%20with%20natural%20light%20clean%20modern%20fashion%20catalog%20photography%20with%20refined%20editorial%20composition&width=1920&height=1080&seq=pandabuy-article-hero-jackets-v2&orientation=landscape",
        imageAlt: "Pandabuy Spreadsheet Jackets Guide"
      },
      body: null
    },

    // Article 10: Pants Guide
    10: {
      id: 10,
      hero: {
        breadcrumb: ["Home", "Blog", "Pants & Denim"],
        h1Lines: ["PANDABUY SHEET", "PANTS & DENIM", "FIT GUIDE"],
        date: "June 5, 2026",
        readTime: "9 min read",
        category: "Pants & Denim",
        imageUrl: "https://readdy.ai/api/search-image?query=Elegant%20overhead%20composition%20of%20three%20pairs%20of%20trousers%20in%20beige%20navy%20and%20charcoal%20draped%20artfully%20across%20a%20warm%20wooden%20herringbone%20floor%20with%20a%20vintage%20brass%20measuring%20tape%20coiled%20nearby%20soft%20natural%20window%20light%20creating%20gentle%20texture%20shadows%20sophisticated%20tailoring%20editorial%20photography&width=1920&height=1080&seq=pandabuy-article-hero-pants-v2&orientation=landscape",
        imageAlt: "Pandabuy Spreadsheet Pants Guide"
      },
      body: null
    },

    // Article 11: Accessories Guide
    11: {
      id: 11,
      hero: {
        breadcrumb: ["Home", "Blog", "Accessories"],
        h1Lines: ["PANDABUY SHEET", "ACCESSORIES GUIDE", "WATCHES & JEWELRY"],
        date: "June 3, 2026",
        readTime: "7 min read",
        category: "Accessories",
        imageUrl: "https://readdy.ai/api/search-image?query=Carefully%20styled%20still%20life%20of%20premium%20accessories%20including%20a%20minimalist%20watch%20leather%20bracelet%20and%20sunglasses%20arranged%20on%20textured%20grey%20linen%20fabric%20with%20a%20single%20monstera%20leaf%20for%20organic%20contrast%20soft%20directional%20light%20from%20above%20creating%20refined%20gallery%20worthy%20editorial%20composition&width=1920&height=1080&seq=pandabuy-article-hero-accessories-v2&orientation=landscape",
        imageAlt: "Pandabuy Spreadsheet Accessories Guide"
      },
      body: null
    },

    // Article 12: Bags Guide
    12: {
      id: 12,
      hero: {
        breadcrumb: ["Home", "Blog", "Bags"],
        h1Lines: ["PANDABUY SHEET", "BAGS & BACKPACKS", "BUYING DIRECTORY"],
        date: "June 1, 2026",
        readTime: "10 min read",
        category: "Bags",
        imageUrl: "https://readdy.ai/api/search-image?query=Collection%20of%20four%20stylish%20bags%20in%20different%20silhouettes%20arranged%20on%20a%20warm%20walnut%20bench%20seat%20against%20a%20soft%20sage%20colored%20wall%20trailing%20ivy%20cascading%20from%20above%20bright%20natural%20daylight%20creating%20crisp%20boutique%20style%20product%20presentation%20with%20refined%20lifestyle%20photography%20aesthetic&width=1920&height=1080&seq=pandabuy-article-hero-bags-v2&orientation=landscape",
        imageAlt: "Pandabuy Spreadsheet Bags Guide"
      },
      body: null
    },

    // Article 13: Perfumes Guide
    13: {
      id: 13,
      hero: {
        breadcrumb: ["Home", "Blog", "Perfumes"],
        h1Lines: ["PANDABUY SHEET", "PERFUMES & FRAGRANCES", "SCENT GUIDE"],
        date: "May 28, 2026",
        readTime: "8 min read",
        category: "Perfumes",
        imageUrl: "https://readdy.ai/api/search-image?query=Three%20elegant%20glass%20perfume%20bottles%20with%20amber%20colored%20fragrances%20arranged%20on%20a%20dark%20polished%20marble%20surface%20with%20delicate%20white%20flower%20petals%20scattered%20around%20and%20a%20single%20lit%20candle%20creating%20warm%20ambient%20glow%20intimate%20luxurious%20sensory%20editorial%20photography%20with%20french%20parfumerie%20aesthetic&width=1920&height=1080&seq=pandabuy-article-hero-perfumes-v2&orientation=landscape",
        imageAlt: "Pandabuy Spreadsheet Perfumes Guide"
      },
      body: null
    },

    // Article 14: Safety Guide
    14: {
      id: 14,
      hero: {
        breadcrumb: ["Home", "Blog", "Safety Guide"],
        h1Lines: ["PANDABUY SHEET", "SAFETY TIPS", "SHOP SMART GUIDE"],
        date: "May 25, 2026",
        readTime: "12 min read",
        category: "Safety Guide",
        imageUrl: "https://readdy.ai/api/search-image?query=Clean%20modern%20composition%20featuring%20a%20laptop%20with%20shield%20icon%20glowing%20on%20screen%20beside%20a%20steaming%20ceramic%20cup%20of%20tea%20and%20a%20small%20potted%20snake%20plant%20on%20a%20light%20oak%20desk%20surface%20soft%20daylight%20through%20sheer%20curtains%20creating%20calm%20secure%20atmosphere%20conveying%20online%20shopping%20safety%20and%20digital%20protection&width=1920&height=1080&seq=pandabuy-article-hero-safety-v2&orientation=landscape",
        imageAlt: "Pandabuy Spreadsheet Safety Guide"
      },
      body: null
    },
  };

  return map[id] || null;
}